import React, { useEffect } from "react";
import { Outlet, useMatches } from "react-router-dom";
import * as Sentry from "@sentry/react";
import Backdrop from "@frontend/app/layout/backdrops/Backdrop.tsx";
import MainNav from "../navigation/MainNav.tsx";
import Splash from "@frontend/app/layout/splash/Splash.tsx";
import MetaTags from "@frontend/app/seo/MetaTags.tsx";

const RootLayout = () => {
  // Get all matched routes
  const matches = useMatches();

  // Find the first route with meta data
  const meta = matches.find(match => match.handle?.meta)?.handle?.meta;

  useEffect(() => {
    // Skip delay if window.__SKIP_SPLASH__ is set (e.g., in tests)
    if ((window as any).__SKIP_SPLASH__) {
      document.body.classList.add("visited");
      return;
    }

    setTimeout(() => {
      document.body.classList.add("visited");
    }, 2800);
  }, []);

  // Hidden Sentry test trigger - keyboard shortcut (Ctrl+Shift+E or Cmd+Shift+E)
  // Just throw an error - Sentry will automatically capture it
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+E (or Cmd+Shift+E on Mac) to trigger test error
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "E" || e.key === "e")) {
        e.preventDefault();
        e.stopPropagation();
        
        // Make each error unique to prevent Sentry from grouping them
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(7);
        
        // Just throw - Sentry will automatically capture unhandled errors
        throw new Error(`Test error from keyboard shortcut - ${timestamp} - ${randomId}`);
      }
    };

    // Use capture phase and attach to document for better reliability
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  return (
    <div className="relative h-screen bg-grey-90 w-full overflow-hidden">
      {meta && <MetaTags {...meta} />}

      <Splash />
      <div>
        <Backdrop />
      </div>
      {/* Strict viewport-locked wrapper - prevents any scroll-related layout shifts */}
      <div className="fixed inset-0 z-10 overflow-hidden">
        <div className="relative h-full w-full flex flex-col">
          <MainNav />
          <div className="flex-grow w-full flex justify-center px-4 overflow-y-auto">
            <div className="w-full max-w-screen-xl lg:min-w-[50em]">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
      {/* Sentry test trigger: Ctrl+Shift+E (or Cmd+Shift+E on Mac) */}
    </div>
  );
};

export default RootLayout;
